import React from "react";
import { CheckIcon, X, Settings, Tag, User, Trash2, Download, Mail } from "lucide-react";
import type { BulkActionGroup, BulkActionWithOnClick } from "./types";
import {
  BULK_ACTION_LABELS,
  BULK_ACTION_IDS,
  BULK_ACTION_GROUPS,
  BULK_ACTION_GROUP_LABELS,
} from "@/lib/constants/bulk-actions";

/**
 * Helper function to create pre-configured action groups for common entity types
 */
export const createContactBulkActionGroups = (
  onBulkUpdateStatus: (itemIds: string[], status: string) => Promise<void>,
  onBulkUpdateType: (itemIds: string[], type: string) => Promise<void>,
  onBulkActivate: (itemIds: string[]) => Promise<void>,
  onBulkDeactivate: (itemIds: string[]) => Promise<void>,
  onBulkAddTags: (itemIds: string[], tags: string[]) => Promise<void>,
  onBulkExport: (itemIds: string[]) => Promise<void>,
  onBulkExportEmails: () => Promise<void>,
  onBulkDelete: (itemIds: string[]) => Promise<void>
): BulkActionGroup<any>[] => [
  {
    id: BULK_ACTION_GROUPS.QUICK,
    label: BULK_ACTION_GROUP_LABELS.QUICK,
    icon: <CheckIcon className="h-4 w-4" />,
    actions: [
      {
        id: BULK_ACTION_IDS.ACTIVATE,
        label: BULK_ACTION_LABELS.ACTIVATE,
        icon: <CheckIcon className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          await onBulkActivate(selectedIds);
        },
      } as BulkActionWithOnClick<any>,
      {
        id: BULK_ACTION_IDS.DEACTIVATE,
        label: BULK_ACTION_LABELS.DEACTIVATE,
        icon: <X className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          await onBulkDeactivate(selectedIds);
        },
      } as BulkActionWithOnClick<any>,
    ],
  },
  {
    id: BULK_ACTION_GROUPS.STATUS,
    label: BULK_ACTION_GROUP_LABELS.STATUS,
    icon: <Settings className="h-4 w-4" />,
    actions: [
      {
        id: BULK_ACTION_IDS.UPDATE_STATUS,
        label: BULK_ACTION_LABELS.UPDATE_STATUS,
        icon: <Settings className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          const newStatus = prompt("Enter new status (customer/lead/prospect):");
          if (newStatus) {
            await onBulkUpdateStatus(selectedIds, newStatus);
          }
        },
      } as BulkActionWithOnClick<any>,
      {
        id: BULK_ACTION_IDS.UPDATE_TYPE,
        label: BULK_ACTION_LABELS.UPDATE_TYPE,
        icon: <Tag className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          const newType = prompt("Enter new type (individual/company):");
          if (newType) {
            await onBulkUpdateType(selectedIds, newType);
          }
        },
      } as BulkActionWithOnClick<any>,
    ],
  },
  {
    id: BULK_ACTION_GROUPS.TAGS,
    label: BULK_ACTION_GROUP_LABELS.TAGS,
    icon: <Tag className="h-4 w-4" />,
    actions: [
      {
        id: BULK_ACTION_IDS.ADD_TAGS,
        label: BULK_ACTION_LABELS.ADD_TAGS,
        icon: <Tag className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          const tags = prompt("Enter tags (comma-separated):");
          if (tags) {
            await onBulkAddTags(
              selectedIds,
              tags.split(",").map((t) => t.trim())
            );
          }
        },
      } as BulkActionWithOnClick<any>,
    ],
  },
  {
    id: BULK_ACTION_GROUPS.SYSTEM,
    label: BULK_ACTION_GROUP_LABELS.SYSTEM,
    icon: <Settings className="h-4 w-4" />,
    actions: [
      {
        id: BULK_ACTION_IDS.EXPORT,
        label: BULK_ACTION_LABELS.EXPORT,
        icon: <Download className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          await onBulkExport(selectedIds);
        },
      } as BulkActionWithOnClick<any>,
      {
        id: BULK_ACTION_IDS.EXPORT_EMAILS,
        label: BULK_ACTION_LABELS.EXPORT_EMAILS,
        icon: <Mail className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          await onBulkExportEmails();
        },
      } as BulkActionWithOnClick<any>,
      {
        id: BULK_ACTION_IDS.DELETE,
        label: BULK_ACTION_LABELS.DELETE,
        icon: <Trash2 className="h-4 w-4" />,
        variant: "destructive" as const,
        onClick: async (selectedItems, selectedIds) => {
          if (window.confirm(`Delete ${selectedItems.length} contacts?`)) {
            await onBulkDelete(selectedIds);
          }
        },
      } as BulkActionWithOnClick<any>,
    ],
  },
];

export const createTaskBulkActionGroups = (
  onBulkComplete: (itemIds: string[]) => Promise<void>,
  onBulkAssign: (itemIds: string[], userId: string) => Promise<void>,
  onBulkUpdatePriority: (itemIds: string[], priority: string) => Promise<void>,
  onBulkAddTags: (itemIds: string[], tags: string[]) => Promise<void>,
  onBulkExport: (itemIds: string[]) => Promise<void>,
  onBulkDelete: (itemIds: string[]) => Promise<void>
): BulkActionGroup<any>[] => [
  {
    id: BULK_ACTION_GROUPS.QUICK,
    label: BULK_ACTION_GROUP_LABELS.QUICK,
    icon: <CheckIcon className="h-4 w-4" />,
    actions: [
      {
        id: BULK_ACTION_IDS.COMPLETE,
        label: BULK_ACTION_LABELS.MARK_COMPLETE,
        icon: <CheckIcon className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          await onBulkComplete(selectedIds);
        },
      } as BulkActionWithOnClick<any>,
    ],
  },
  {
    id: BULK_ACTION_GROUPS.ASSIGN,
    label: BULK_ACTION_GROUP_LABELS.ASSIGN,
    icon: <User className="h-4 w-4" />,
    actions: [
      {
        id: BULK_ACTION_IDS.ASSIGN,
        label: BULK_ACTION_LABELS.ASSIGN_TO_USER,
        icon: <User className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          const userId = prompt("Enter user ID to assign to:");
          if (userId) {
            await onBulkAssign(selectedIds, userId);
          }
        },
      } as BulkActionWithOnClick<any>,
    ],
  },
  {
    id: BULK_ACTION_GROUPS.PRIORITY,
    label: BULK_ACTION_GROUP_LABELS.PRIORITY,
    icon: <Settings className="h-4 w-4" />,
    actions: [
      {
        id: BULK_ACTION_IDS.UPDATE_PRIORITY,
        label: BULK_ACTION_LABELS.UPDATE_PRIORITY,
        icon: <Settings className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          const priority = prompt("Enter priority (low/medium/high/critical):");
          if (priority) {
            await onBulkUpdatePriority(selectedIds, priority);
          }
        },
      } as BulkActionWithOnClick<any>,
    ],
  },
  {
    id: BULK_ACTION_GROUPS.SYSTEM,
    label: BULK_ACTION_GROUP_LABELS.SYSTEM,
    icon: <Settings className="h-4 w-4" />,
    actions: [
      {
        id: BULK_ACTION_IDS.EXPORT,
        label: BULK_ACTION_LABELS.EXPORT,
        icon: <Download className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          await onBulkExport(selectedIds);
        },
      } as BulkActionWithOnClick<any>,
      {
        id: BULK_ACTION_IDS.DELETE,
        label: BULK_ACTION_LABELS.DELETE,
        icon: <Trash2 className="h-4 w-4" />,
        variant: "destructive" as const,
        onClick: async (selectedItems, selectedIds) => {
          if (window.confirm(`Delete ${selectedItems.length} tasks?`)) {
            await onBulkDelete(selectedIds);
          }
        },
      } as BulkActionWithOnClick<any>,
    ],
  },
];

export const createCompanyBulkActionGroups = (
  onBulkExport: (itemIds: string[]) => Promise<void>,
  onBulkExportEmails: () => Promise<void>,
  onBulkDelete: (itemIds: string[]) => Promise<void>
): BulkActionGroup<any>[] => [
  {
    id: BULK_ACTION_GROUPS.SYSTEM,
    label: BULK_ACTION_GROUP_LABELS.SYSTEM,
    icon: <Settings className="h-4 w-4" />,
    actions: [
      {
        id: BULK_ACTION_IDS.EXPORT,
        label: BULK_ACTION_LABELS.EXPORT,
        icon: <Download className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          await onBulkExport(selectedIds);
        },
      } as BulkActionWithOnClick<any>,
      {
        id: BULK_ACTION_IDS.EXPORT_EMAILS,
        label: BULK_ACTION_LABELS.EXPORT_EMAILS,
        icon: <Mail className="h-4 w-4" />,
        onClick: async (selectedItems, selectedIds) => {
          await onBulkExportEmails();
        },
      } as BulkActionWithOnClick<any>,
      {
        id: BULK_ACTION_IDS.DELETE,
        label: BULK_ACTION_LABELS.DELETE,
        icon: <Trash2 className="h-4 w-4" />,
        variant: "destructive" as const,
        onClick: async (selectedItems, selectedIds) => {
          if (window.confirm(`Delete ${selectedItems.length} companies?`)) {
            await onBulkDelete(selectedIds);
          }
        },
      } as BulkActionWithOnClick<any>,
    ],
  },
];
